import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useScrollReveal } from '@/hooks/use-scroll-reveal'
import Container from '@/components/ui/container'
import Icon from '@/components/ui/icon'

interface ServiceOption {
  label: string
  value: string
}

export default function ContactForm() {
  const { t } = useTranslation()
  const { ref, visible } = useScrollReveal()
  const services = t('contact.form.services', { returnObjects: true }) as ServiceOption[]

  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: '',
  })
  const [submitted, setSubmitted] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(false)

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSending(true)
    setError(false)

    const serviceLabel = services.find((s) => s.value === form.service)?.label ?? form.service

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: 'f38a79de-d5c3-42c7-92ad-2beb63c23453',
          subject: `Contato - ${form.company} - ${serviceLabel}`,
          from_name: form.name,
          name: form.name,
          email: form.email,
          company: form.company,
          service: serviceLabel,
          message: form.message,
        }),
      })

      if (res.ok) {
        setSubmitted(true)
      } else {
        setError(true)
      }
    } catch {
      setError(true)
    } finally {
      setSending(false)
    }
  }

  if (submitted) {
    return (
      <section ref={ref} className="py-16 md:py-24 lg:py-32">
        <Container>
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-16 h-16 mx-auto mb-8 flex items-center justify-center border border-primary/30">
              <Icon name="check_circle" className="text-4xl text-primary" />
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter text-on-surface mb-4">
              {t('contact.form.successHeading')}
            </h2>
            <p className="text-on-surface-variant text-lg">{t('contact.form.successMessage')}</p>
          </div>
        </Container>
      </section>
    )
  }

  return (
    <section ref={ref} className="py-16 md:py-24 lg:py-32">
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 lg:gap-16">
          <div
            className={`lg:col-span-4 transition-all duration-700 ease-out ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <span className="text-primary font-body text-label-md uppercase tracking-[0.4em] mb-4 block">
              {t('contact.form.label')}
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter text-on-surface mb-6">
              {t('contact.form.heading')}
            </h2>
            <p className="text-on-surface-variant text-sm leading-relaxed">
              {t('contact.form.description')}
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className={`lg:col-span-8 space-y-8 transition-all duration-700 ease-out ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: visible ? '200ms' : '0ms' }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label className="text-on-surface-variant text-xs uppercase tracking-[0.2em] font-medium block">
                  {t('contact.form.nameLabel')}
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={form.name}
                  onChange={handleChange}
                  placeholder={t('contact.form.namePlaceholder')}
                  className="w-full bg-transparent border-b border-outline-variant/30 focus:border-primary pb-3 text-on-surface placeholder:text-on-surface-variant/40 outline-none transition-colors text-sm"
                />
              </div>

              <div className="space-y-2">
                <label className="text-on-surface-variant text-xs uppercase tracking-[0.2em] font-medium block">
                  {t('contact.form.emailLabel')}
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  placeholder={t('contact.form.emailPlaceholder')}
                  className="w-full bg-transparent border-b border-outline-variant/30 focus:border-primary pb-3 text-on-surface placeholder:text-on-surface-variant/40 outline-none transition-colors text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label className="text-on-surface-variant text-xs uppercase tracking-[0.2em] font-medium block">
                  {t('contact.form.companyLabel')}
                </label>
                <input
                  type="text"
                  name="company"
                  required
                  value={form.company}
                  onChange={handleChange}
                  placeholder={t('contact.form.companyPlaceholder')}
                  className="w-full bg-transparent border-b border-outline-variant/30 focus:border-primary pb-3 text-on-surface placeholder:text-on-surface-variant/40 outline-none transition-colors text-sm"
                />
              </div>

              <div className="space-y-2">
                <label className="text-on-surface-variant text-xs uppercase tracking-[0.2em] font-medium block">
                  {t('contact.form.serviceLabel')}
                </label>
                <select
                  name="service"
                  required
                  value={form.service}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-outline-variant/30 focus:border-primary pb-3 text-on-surface outline-none transition-colors text-sm cursor-pointer appearance-none"
                >
                  <option
                    value=""
                    disabled
                    className="bg-surface-container text-on-surface-variant"
                  >
                    {t('contact.form.servicePlaceholder')}
                  </option>
                  {services.map((s) => (
                    <option key={s.value} value={s.value} className="bg-surface-container">
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-on-surface-variant text-xs uppercase tracking-[0.2em] font-medium block">
                {t('contact.form.messageLabel')}
              </label>
              <textarea
                name="message"
                required
                rows={4}
                value={form.message}
                onChange={handleChange}
                placeholder={t('contact.form.messagePlaceholder')}
                className="w-full bg-transparent border-b border-outline-variant/30 focus:border-primary pb-3 text-on-surface placeholder:text-on-surface-variant/40 outline-none transition-colors text-sm resize-none"
              />
            </div>

            {error && <p className="text-red-400 text-sm">{t('contact.form.errorMessage')}</p>}

            <div className="pt-4">
              <button
                type="submit"
                disabled={sending}
                className="bg-primary text-on-primary px-10 py-4 font-bold uppercase tracking-widest text-sm hover:bg-primary-fixed transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? t('contact.form.sendingButton') : t('contact.form.submitButton')}
              </button>
            </div>
          </form>
        </div>
      </Container>
    </section>
  )
}
